import { ChangeDetectionStrategy, Component, HostListener, OnInit, Renderer2 } from '@angular/core';
import { SSCState } from './reducers';
import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SessionService } from './core/services/session/session.service';
import { first } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { TrackingService } from './core/services/tracking/tracking.service';
import { Meta, Title } from '@angular/platform-browser';
import { CanonicalService } from './core/services/canonical.service';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentService } from './core/services/environment/environment.service';
import { FeatureService } from './core/services/features/feature.service';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'ssc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  constructor(
    private store: Store<SSCState>,
    private sessionService: SessionService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private metaTagService: Meta,
    private titleTagService: Title,
    private canonicalService: CanonicalService,
    private renderer: Renderer2,
    private entryService: EnvironmentService,
    private featureService: FeatureService,
  ) {}

  @HostListener('document:visibilitychange')
  // Visibility change fires in most but not all cases on mobile (https://caniuse.com/mdn-api_document_visibilitychange_event)
  @HostListener('window:pagehide')
  public onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.store.pipe(first(), untilDestroyed(this)).subscribe((state) => this.sessionService.saveState(state));
    }
  }

  public ngOnInit(): void {
    this.entryService
      .getEnvironment$()
      .pipe(untilDestroyed(this))
      .subscribe((env) => {
        if (env.environment === 'PRD01') {
          this.addConsentCookieScript();
        }
      });
    this.setTitle();
    this.addMetaTags();
    this.canonicalService.setCanonicalURL('https://dap.visana.ch/offer/');
    this.sessionService.saveSessionId();
    TrackingService.initializeTracking();

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      window.location.replace(window.location.pathname);
    });
    this.featureService.loadFeatureFlags();
  }

  private setTitle(): void {
    this.titleTagService.setTitle(this.translateService.instant('WEBSITE.SEO.TITLE'));
  }

  private addConsentCookieScript(): void {
    const script = this.renderer.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.src = 'https://consent.cookiefirst.com/sites/dap.visana.ch-b190c5cd-57e9-4fa1-a766-83c4311c3fbd/consent.js';
    script.setAttribute('data-language', 'inherit');

    const head = document.getElementsByTagName('head')[0];
    const firstChild = head.firstChild;
    if (firstChild) {
      head.insertBefore(script, firstChild);
    } else {
      head.appendChild(script);
    }
  }

  private addMetaTags(): void {
    this.metaTagService.addTags([
      { charset: 'UTF-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'robots', content: 'index, follow' },
      {
        name: 'description',
        content: this.translateService.instant('WEBSITE.SEO.DESCRIPTION'),
      },
      {
        name: 'google-site-verification',
        content: 'vkW7HGt7pXAv9XY_V8b8cTVBfee-zTy9wiUFjxeso-8',
      },
    ]);
  }
}
